import React, { useState, useEffect, useMemo } from'react';
import { useSearchParams } from'react-router-dom';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import ExportButton from'../components/ExportButton';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import { InlineLoader } from'../components/PageLoader';
import {
 Search,
 MessageSquare,
 User,
 Bot,
 Hash,
 Clock,
 Copy,
 Check,
 X,
 AlertCircle,
 AlertTriangle,
 Activity,
 Coins,
 Users as UsersIcon,
 ChevronRight,
 Inbox,
 ArrowLeft,
} from'lucide-react';

/**
 * Conversation Inspector
 *
 * Two-pane layout: app's recent calls on the left, deep call detail
 * on the right. The detail panel renders the OpenAI request/response as a
 * chat transcript when possible, with fallbacks to raw JSON for everything
 * else (tool calls, embeddings, non-chat completions, errors, …).
 */
export default function ConversationInspector() {
 const { timeRange, setTimeRange } = useLayout();
 const [searchParams, setSearchParams] = useSearchParams();
 const urlSubscriptionId = searchParams.get('subscriptionId') ||'';
 const urlOperationId = searchParams.get('operationId') ||'';

 const [subscriptionId, setSubscriptionId] = useState(urlSubscriptionId);
 const [searchInput, setSearchInput] = useState(urlSubscriptionId);
 const [selectedOp, setSelectedOp] = useState(urlOperationId || null);
 const [detailTab, setDetailTab] = useState('chat'); //'chat' |'raw' |'meta'

 // APIM subscription-id / Entra app client-id → friendly app name. The KQL
 // column conflates both namespaces (see backend routes/conversations.js)
 // so the backend resolves through APIM Management *and* Microsoft Graph
 // and returns whichever wins. The landing and recent feeds already include
 //`appName` inline; this query only fires for deep-link cases where the
 // user lands on`?subscriptionId=...` directly and we don't have a row
 // to pull the name from.
 const { data: appsList } = useApiQuery(
 ['conversation-apps', subscriptionId ||''],
 () => api.getConversationApps(subscriptionId ? [subscriptionId] : []),
 { enabled: !!subscriptionId, staleTime: 5 * 60 * 1000 },
 );
 const appNameById = useMemo(() => {
 const m = new Map();
 for (const a of Array.isArray(appsList) ? appsList : []) {
 if (!a?.id || !a?.name) continue;
 m.set(a.id, a.name);
 m.set(a.id.toLowerCase(), a.name);
 }
 return m;
 }, [appsList]);
 const resolveAppName = (id) => {
 if (!id) return null;
 const s = String(id);
 // Backend buckets token traces with no APIM`Subscription ID` header
 // and no`appClientId` under this sentinel so they're still surfaced
 // (otherwise the Conversations page would silently drop calls that
 // the dashboard's Total Tokens KPI counts). Render with a friendly
 // label rather than the raw token.
 if (s.toLowerCase() ==='unattributed') return'Unattributed';
 return appNameById.get(s) || appNameById.get(s.toLowerCase()) || null;
 };
 const activeAppName = resolveAppName(subscriptionId);

 // The global timeRange (from useLayout) is shared across every page via
 // the TopBar picker, so whatever the user picked on the previous page
 // is what we use here. We deliberately do NOT widen it on mount — an
 // earlier version of this page auto-bumped 24h up to 7d"to make the
 // inspector more useful", but that silently overrode the picker every
 // time the user navigated to /conversations and made the time-range
 // pill jump in the UI. If the user wants a wider window they can use
 // the top-bar picker (or the per-row"Widen to 30d" affordance below).

 // Keep state in sync with URL params for back/forward navigation, deep
 // links, AND sidebar re-clicks on the Conversations link (which navigate
 // to`/conversations` with no params and should reset the view).
 useEffect(() => {
 if (urlSubscriptionId !== subscriptionId) {
 setSubscriptionId(urlSubscriptionId);
 setSearchInput(urlSubscriptionId);
 }
 if ((urlOperationId || null) !== selectedOp) {
 setSelectedOp(urlOperationId || null);
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [urlSubscriptionId, urlOperationId]);

 const {
 data: conversations,
 isPending: conversationsPending,
 isFetching: conversationsFetching,
 error: conversationsError,
 } = useApiQuery(
 ['conversations', subscriptionId, timeRange],
 () => api.getConversations(subscriptionId, timeRange, 200),
 { enabled: !!subscriptionId }
 );

 // Header KPIs (Calls / Total Tokens). Sourced from a dedicated uncapped
 // aggregation endpoint, NOT from`conversations.length` — that list is
 // capped at 200 rows for table responsiveness, and using its length as
 // the call count silently under-reports any busy app. The totals
 // endpoint counts AppRequests rows, matching Cost Center exactly.
 const { data: totals } = useApiQuery(
 ['conversation-totals', subscriptionId, timeRange],
 () => api.getConversationTotals(subscriptionId, timeRange),
 { enabled: !!subscriptionId }
 );

 const {
 data: callDetail,
 isPending: callDetailPending,
 isFetching: callDetailFetching,
 error: callDetailError,
 } = useApiQuery(
 ['call-detail', selectedOp],
 () => api.getCallDetail(selectedOp),
 { enabled: !!selectedOp }
 );

 const conversationsLoading =
 !!subscriptionId && (conversationsPending || conversationsFetching) && !conversations;
 const callDetailLoading =
 !!selectedOp && (callDetailPending || callDetailFetching) && !callDetail;

 const handleSearch = (e) => {
 e.preventDefault();
 const v = (searchInput ||'').trim();
 if (!v) return;
 // The input field expects an id (subscription id / app client id), not a
 // friendly name — names aren't unique and aren't indexed. Users normally
 // pick an app from the landing list; this textbox is for paste-an-id
 // workflows (deep links,"find the call that produced this id", etc.).
 // Manual lookup should default to a useful window. Don't shrink the
 // user's current selection — only widen if it's too narrow.
 setTimeRange((cur) => widenTimeRange(cur,'7d'));
 setSubscriptionId(v);
 setSelectedOp(null);
 setSearchParams({ subscriptionId: v });
 };

 const handleSelectCall = (operationId) => {
 setSelectedOp(operationId);
 setDetailTab('chat');
 if (subscriptionId) {
 setSearchParams({ subscriptionId, operationId });
 }
 };

 const handleClearCall = () => {
 setSelectedOp(null);
 if (subscriptionId) setSearchParams({ subscriptionId });
 };

 /** Drop all selection state and return to the landing browser. */
 const handleResetToLanding = () => {
 setSubscriptionId('');
 setSearchInput('');
 setSelectedOp(null);
 setSearchParams({});
 };

 // Aggregate stats for the header strip.
 //
 //`calls`,`totalTokens`,`promptTokens`,`completionTokens`,
 //`uniqueUsers` come from the dedicated /conversations/totals endpoint
 // (uncapped, AppRequests-based) so they agree with Cost Center.
 // The`models` array is derived from the loaded conversations because
 // it's only used to populate the model-filter dropdown — that's fine
 // to base on the visible (≤200) list.
 const stats = useMemo(() => {
 if (!totals && (!Array.isArray(conversations) || conversations.length === 0)) {
 return null;
 }
 const models = new Set();
 if (Array.isArray(conversations)) {
 for (const c of conversations) if (c.model) models.add(c.model);
 }
 return {
 calls: totals?.calls ?? (Array.isArray(conversations) ? conversations.length : 0),
 totalTokens: totals?.totalTokens ?? 0,
 promptTokens: totals?.promptTokens ?? 0,
 completionTokens: totals?.completionTokens ?? 0,
 uniqueUsers: totals?.uniqueUsers ?? 0,
 models: Array.from(models),
 };
 }, [totals, conversations]);

 return (
 <div className="space-y-6">
 {subscriptionId || selectedOp ? (
 <Breadcrumbs items={[
 { label:'Conversations', href:'/conversations' },
 { label: subscriptionId
 ? (activeAppName ||`App ${truncateMiddle(subscriptionId, 24)}`)
 :`Call ${truncateMiddle(selectedOp, 18)}` },
 ]} />
 ) : (
 <Breadcrumbs />
 )}

 {/* Back button — visible whenever we're inside an app/call view. */}
 {(subscriptionId || selectedOp) && (
 <button
 onClick={handleResetToLanding}
 className="inline-flex items-center gap-1.5 text-xs text-gray-500 
 hover:text-brand-600 transition -mt-3"
 >
 <ArrowLeft className="w-3.5 h-3.5" />
 Back to all apps
 </button>
 )}

 <div className="flex items-start justify-between gap-4 flex-wrap">
 <div>     <h1 className="text-xl font-bold">Conversation Inspector</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 Inspect individual API calls and their prompts, completions, and metadata.
 </p>
 <p className="text-xs text-gray-400 mt-0.5">
 LLM calls only (token-bearing gateway traffic) — for all gateway traffic incl. non-LLM APIs, errors, and edge rejections see API Analytics.
 </p>
 </div>        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto sm:min-w-[280px]">
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
 <input
 type="text"
 value={searchInput ||''}
 onChange={(e) => setSearchInput(e.target.value)}
 placeholder="Enter App ID…"
 className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 
 rounded-lg bg-white focus:ring-2 focus:ring-brand-500
 focus:border-transparent outline-none transition"
 />
 </div>
 <button
 type="submit"
 className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg
 hover:bg-brand-700 transition disabled:opacity-50"
 disabled={!(searchInput ||'').trim()}
 >
 Search
 </button>
 </form>
 </div>

 {/* Landing browser — shown when neither an app nor an operation is in play */}
 {!subscriptionId && !selectedOp && (
 <ErrorBoundary>
 <LandingBrowser
 timeRange={timeRange}
 resolveAppName={resolveAppName}
 onPickSubscription={(sid) => {
 if (!sid) return;
 // Respect the user's current window: the subscription was
 // visible in the landing browser at this range, so its calls
 // exist in the same range. Forcing a widen here would
 // silently override the picker the user just set.
 setSubscriptionId(sid);
 setSearchInput(sid);
 setSelectedOp(null);
 setSearchParams({ subscriptionId: sid });
 }}
 onPickCall={(row) => {
 const sid = row?.subscriptionId ||'';
 const op = row?.operationId ||'';
 if (!sid && !op) return;
 if (sid) {
 setSubscriptionId(sid);
 setSearchInput(sid);
 }
 if (op) setSelectedOp(op);
 setSearchParams({
 ...(sid ? { subscriptionId: sid } : {}),
 ...(op ? { operationId: op } : {}),
 });
 }}
 />
 </ErrorBoundary>
 )}

 {/* Error state */}
 {subscriptionId && conversationsError && (
 <div className="chart-container">
 <div className="flex items-start gap-3 text-red-600">
 <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
 <div>
 <div className="font-medium text-sm">Failed to load conversations</div>
 <div className="text-xs mt-1 text-red-500">
 {conversationsError.message ||'Unknown error'}
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Stat cards */}
 {subscriptionId && stats && (
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <StatCard
 icon={<Activity className="w-4 h-4" />}
 label="Calls"
 value={stats.calls.toLocaleString()}
 />
 <StatCard
 icon={<Coins className="w-4 h-4" />}
 label="Total Tokens"
 value={stats.totalTokens.toLocaleString()}
 sub={`${stats.promptTokens.toLocaleString()} in / ${stats.completionTokens.toLocaleString()} out`}
 />
 <StatCard
 icon={<UsersIcon className="w-4 h-4" />}
 label="Unique Users"
 value={stats.uniqueUsers.toLocaleString()}
 />
 <StatCard
 icon={<Bot className="w-4 h-4" />}
 label="Models"
 value={stats.models.length}
 sub={stats.models.slice(0, 3).join(',') ||'—'}
 />
 </div>
 )}

 {/* Main two-pane layout */}
 {subscriptionId && (
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
 {/* Calls list */}
 <div className="lg:col-span-2">
 <ErrorBoundary>
 <CallsList
 loading={conversationsLoading}
 conversations={conversations}
 selectedOp={selectedOp}
 onSelect={handleSelectCall}
 subscriptionId={subscriptionId}
 appName={activeAppName}
 timeRange={timeRange}
 onWiden={() => setTimeRange((cur) => widenTimeRange(cur,'30d'))}
 />
 </ErrorBoundary>
 </div>

 {/* Detail panel */}
 <div className="lg:col-span-3">
 <ErrorBoundary>
 <CallDetailPanel
 operationId={selectedOp}
 loading={callDetailLoading}
 entries={callDetail}
 error={callDetailError}
 tab={detailTab}
 onTabChange={setDetailTab}
 onClose={handleClearCall}
 resolveAppName={resolveAppName}
 />
 </ErrorBoundary>
 </div>
 </div>
 )}

 {/* Operation-only deep link (e.g. from Real-Time or User Activity) */}
 {!subscriptionId && selectedOp && (
 <ErrorBoundary>
 <CallDetailPanel
 operationId={selectedOp}
 loading={callDetailLoading}
 entries={callDetail}
 error={callDetailError}
 tab={detailTab}
 onTabChange={setDetailTab}
 onClose={handleClearCall}
 resolveAppName={resolveAppName}
 onJumpToSubscription={(sid) => {
 setSubscriptionId(sid);
 setSearchInput(sid);
 setSearchParams({ subscriptionId: sid, operationId: selectedOp });
 }}
 />
 </ErrorBoundary>
 )}
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub }) {
 return (
 <div className="chart-container">
 <div className="flex items-center gap-2 text-xs text-gray-500">
 {icon}
 <span>{label}</span>
 </div>
 <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
 {sub && (
 <div className="mt-1 text-xs text-gray-400 truncate" title={sub}>
 {sub}
 </div>
 )}
 </div>
 );
}

/**
 * Default landing view shown when no app or operation is in the URL.
 * Two columns:
 * - Apps active in the time range, ranked by recency.
 * - Recent calls across all apps (drill-through).
 * The window is narrowed (7d / 24h) to keep these queries cheap regardless
 * of the global TimeRangePicker selection.
 */
function LandingBrowser({ timeRange, resolveAppName, onPickSubscription, onPickCall }) {
 // Both queries respect the user's global time range. The Apps list is
 // an aggregate (one row per subscription) so it stays fast even at 90d.
 // Recent Calls is intentionally capped at the most recent 100 rows by
 // the backend`take 100`, so widening the window only changes which
 // 100 rows come back, not the query cost.
 const subsTimeRange = timeRange;
 const recentTimeRange = timeRange;

 const { data: subsRaw, isPending: subsPending, error: subsError } = useApiQuery(
 ['conversation-subscriptions', subsTimeRange],
 () => api.getConversationSubscriptions(subsTimeRange, 200),
 );
 const { data: recent, isPending: recentPending, error: recentError } = useApiQuery(
 ['recent-conversations', recentTimeRange],
 () => api.getRecentConversations(recentTimeRange, 100),
 );

 // Defensive dedupe: even though the backend now normalizes (trim+lowercase)
 // before grouping, we collapse any same-keyed rows on the client too so a
 // future upstream regression can't leak duplicates into the UI.
 const subs = useMemo(() => {
 if (!Array.isArray(subsRaw)) return subsRaw;
 const byId = new Map();
 for (const s of subsRaw) {
 const id = (s?.subscriptionId ||'').trim().toLowerCase();
 if (!id) continue;
 const existing = byId.get(id);
 if (!existing) {
 byId.set(id, { ...s, subscriptionId: id });
 } else {
 // Merge stats; keep the most recent LastSeen.
 existing.calls += Number(s.calls) || 0;
 existing.totalTokens += Number(s.totalTokens) || 0;
 existing.uniqueUsers = Math.max(existing.uniqueUsers || 0, Number(s.uniqueUsers) || 0);
 existing.uniqueModels = Math.max(existing.uniqueModels || 0, Number(s.uniqueModels) || 0);
 if (!existing.lastSeen || (s.lastSeen && s.lastSeen > existing.lastSeen)) {
 existing.lastSeen = s.lastSeen;
 existing.topModel = s.topModel || existing.topModel;
 }
 if (!existing.firstSeen || (s.firstSeen && s.firstSeen < existing.firstSeen)) {
 existing.firstSeen = s.firstSeen;
 }
 }
 }
 return Array.from(byId.values()).sort((a, b) =>
 (b.lastSeen ||'').localeCompare(a.lastSeen ||'')
 );
 }, [subsRaw]);

 return (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 <div className="chart-container">
 <div className="flex items-center justify-between mb-3">
 <div>
 <h3 className="text-sm font-semibold text-gray-700">Apps</h3>
 <p className="text-[11px] text-gray-400 mt-0.5">
 Active in the last {subsTimeRange} · click to inspect
 </p>
 </div>
 <span className="text-xs text-gray-400 tabular-nums">
 {Array.isArray(subs) ? subs.length.toLocaleString() : 0}
 </span>
 </div>
 {subsError ? (
 <LandingError message={subsError.message} />
 ) : subsPending ? (
 <InlineLoader message="Loading apps…" />
 ) : !Array.isArray(subs) || subs.length === 0 ? (
 <LandingEmpty
 icon={<Inbox className="w-5 h-5" />}
 title="No apps found"
 sub={`No conversation traffic in the last ${subsTimeRange}.`}
 />
 ) : (
 <div className="max-h-[640px] overflow-y-auto -mx-1 space-y-1">
 {subs.map((s) => (
 <SubscriptionRow
 key={s.subscriptionId}
 sub={s}
 appName={s.appName || (resolveAppName && resolveAppName(s.subscriptionId))}
 onClick={() => onPickSubscription(s.subscriptionId)}
 />
 ))}
 </div>
 )}
 </div>

 <div className="chart-container">
 <div className="flex items-center justify-between mb-3">
 <div>
 <h3 className="text-sm font-semibold text-gray-700">Recent Calls</h3>
 <p className="text-[11px] text-gray-400 mt-0.5">
 Across all apps · last {recentTimeRange}
 </p>
 </div>
 <span className="text-xs text-gray-400 tabular-nums">
 {Array.isArray(recent) ? recent.length.toLocaleString() : 0}
 </span>
 </div>
 {recentError ? (
 <LandingError message={recentError.message} />
 ) : recentPending ? (
 <InlineLoader message="Loading recent calls…" />
 ) : !Array.isArray(recent) || recent.length === 0 ? (
 <LandingEmpty
 icon={<MessageSquare className="w-5 h-5" />}
 title="No recent calls"
 sub={`No conversation calls in the last ${recentTimeRange}.`}
 />
 ) : (
 <div className="max-h-[640px] overflow-y-auto -mx-1 space-y-1">
 {recent.map((r) => (
 <RecentCallRow
 key={r.operationId ||`${r.subscriptionId}-${r.timestamp}`}
 call={r}
 appName={r.appName || (resolveAppName && resolveAppName(r.subscriptionId))}
 onClick={() => onPickCall(r)}
 />
 ))}
 </div>
 )}
 </div>
 </div>
 );
}

function SubscriptionRow({ sub, appName, onClick }) {
 return (
 <button
 onClick={onClick}
 className="w-full text-left p-3 rounded-lg border border-transparent transition group
 hover:bg-gray-50 
 hover:border-gray-200"
 >
 <div className="flex items-center justify-between gap-2">
 <div className="min-w-0 flex-1">
 {appName ? (
 <>
 <div className="text-sm font-medium truncate text-gray-800"
 title={appName}>
 {appName}
 </div>
 <div className="font-mono text-[10px] truncate text-gray-400"
 title={sub.subscriptionId}>
 {sub.subscriptionId}
 </div>
 </>
 ) : (
 <span className="font-mono text-xs truncate block text-gray-700"
 title={sub.subscriptionId}>
 {sub.subscriptionId}
 </span>
 )}
 </div>
 <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
 </div>
 <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
 <span className="tabular-nums">
 <span className="font-semibold text-gray-700">
 {Number(sub.calls || 0).toLocaleString()}
 </span> calls
 </span>
 {sub.totalTokens > 0 && (
 <span className="tabular-nums">{Number(sub.totalTokens).toLocaleString()} tok</span>
 )}
 {sub.uniqueUsers > 0 && (
 <span className="tabular-nums">{sub.uniqueUsers} user{sub.uniqueUsers === 1 ?'' :'s'}</span>
 )}
 {sub.topModel && (
 <span className="font-mono text-[10px] px-1.5 py-0.5 rounded
 bg-gray-100 truncate max-w-[160px]">
 {sub.topModel}
 </span>
 )}
 {sub.lastSeen && (
 <span className="ml-auto text-gray-400">{formatRelative(sub.lastSeen)}</span>
 )}
 </div>
 </button>
 );
}

function RecentCallRow({ call, appName, onClick }) {
 const initials = getInitials(call.userName || call.userEmail || call.subscriptionId);
 return (
 <button
 onClick={onClick}
 className="w-full text-left p-3 rounded-lg border border-transparent transition group
 hover:bg-gray-50 
 hover:border-gray-200"
 >
 <div className="flex items-center gap-3">
 <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 
 text-gray-600 flex items-center justify-center
 text-[10px] font-semibold">
 {initials}
 </div>        <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-2">
 <span className="text-sm font-medium truncate">
 {call.userEmail || call.userName || appName ||'App / service call'}
 </span>
 <span className="text-[10px] text-gray-400 flex-shrink-0">
 {call.timestamp ? formatRelative(call.timestamp) :'—'}
 </span>
 </div>
 <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
 {call.model && (
 <span className="font-mono text-[10px] px-1.5 py-0.5 rounded
 bg-gray-100 truncate max-w-[140px]">
 {call.model}
 </span>
 )}
 {call.totalTokens != null && (
 <span className="tabular-nums">{Number(call.totalTokens).toLocaleString()} tok</span>
 )}
 {(appName || call.subscriptionId) && (
 <span className={`truncate max-w-[180px] text-gray-400 ${appName ?'' :'font-mono text-[10px]'}`}
 title={appName ?`${appName} · ${call.subscriptionId}` : call.subscriptionId}>
 {appName || call.subscriptionId}
 </span>
 )}
 </div>
 </div>
 <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
 </div>
 </button>
 );
}

function LandingEmpty({ icon, title, sub }) {
 return (
 <div className="text-center py-10">
 <div className="inline-flex items-center justify-center w-10 h-10 rounded-full
 bg-gray-100 text-gray-400 mb-3">
 {icon}
 </div>
 <div className="text-sm font-medium text-gray-700">{title}</div>
 <div className="text-xs text-gray-500 mt-1">{sub}</div>
 </div>
 );
}

function LandingError({ message }) {
 return (
 <div className="flex items-start gap-2 text-xs text-red-600 py-4">
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{message ||'Failed to load.'}</span>
 </div>
 );
}

// Ordered preset windows, narrowest → widest. Keep in sync with
// TimeRangePicker's RANGES (and the backend's VALID_TIME_RANGES). The
// custom-range token (`custom:<fromISO>:<toISO>`) is intentionally not part
// of this list — see resolveCustomRangeIndex() below.
const RANGE_ORDER = ['5m','15m','1h','6h','24h','7d','30d','90d'];
const RANGE_MS = {
'5m': 5 * 60 * 1000,
'15m': 15 * 60 * 1000,
'1h': 60 * 60 * 1000,
'6h': 6 * 60 * 60 * 1000,
'24h': 24 * 60 * 60 * 1000,
'7d': 7 * 24 * 60 * 60 * 1000,
'30d': 30 * 24 * 60 * 60 * 1000,
'90d': 90 * 24 * 60 * 60 * 1000,
};

/**
 * Map an arbitrary range token to an index into RANGE_ORDER for comparison
 * purposes. Custom ranges (`custom:<fromISO>:<toISO>`) are bucketed by their
 * duration in milliseconds against the preset boundaries — anything wider
 * than 90d is treated as 90d, anything narrower than 5m as 5m. Unknown
 * tokens return -1.
 */
function rangeIndex(token) {
 if (typeof token !=='string') return -1;
 const direct = RANGE_ORDER.indexOf(token);
 if (direct !== -1) return direct;
 if (token.startsWith('custom:')) {
 //`custom:<fromISO>:<toISO>` — ISO strings contain colons too, so a
 // naive`.split(':')` produces more than 3 parts. Use the first and
 // last colon-positions to extract the two ISO timestamps.
 const sep = token.indexOf(':');
 const rest = token.slice(sep + 1);
 const mid = rest.lastIndexOf(':');
 if (mid > 0) {
 const from = new Date(rest.slice(0, mid)).getTime();
 const to = new Date(rest.slice(mid + 1)).getTime();
 if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
 const span = to - from;
 // Pick the smallest preset whose span is >= the custom span. If the
 // custom span is wider than all presets, clamp to the widest.
 for (let i = 0; i < RANGE_ORDER.length; i++) {
 if (span <= RANGE_MS[RANGE_ORDER[i]]) return i;
 }
 return RANGE_ORDER.length - 1;
 }
 }
 // Malformed custom token — refuse to bucket it.
 return -1;
 }
 return -1;
}

/**
 * Clamp the global time-range token to a sensible maximum.
 *
 * Currently unused — the Conversations landing browser used to call this
 * to silently cap its queries to 7d/24h, which caused its KPIs to
 * disagree with Cost Center at long windows. The helper is kept around
 * because`widenTimeRange` below depends on`rangeIndex` and future
 * surfaces (e.g. heavy aggregate widgets) may legitimately need a
 * defensive cap — but new callers must show a visible indicator when
 * the cap clips the user-selected range.
 */
// eslint-disable-next-line no-unused-vars
function clampTimeRange(input, maxRange) {
 const i = rangeIndex(input);
 const j = rangeIndex(maxRange);
 if (j === -1) return maxRange;
 // Unknown input → fall back to the cap rather than silently widening it.
 if (i === -1) return maxRange;
 return i <= j ? input : maxRange;
}

/** Return whichever of`input` and`minRange` represents the wider window. */
function widenTimeRange(input, minRange) {
 const i = rangeIndex(input);
 const j = rangeIndex(minRange);
 if (i === -1) return minRange;
 if (j === -1) return input;
 return i >= j ? input : minRange;
}

/** Truncate a long ID in the middle so both ends remain recognizable. */
function truncateMiddle(s, max = 24) {
 if (!s) return'';
 const str = String(s);
 if (str.length <= max) return str;
 const head = Math.ceil((max - 1) / 2);
 const tail = Math.floor((max - 1) / 2);
 return`${str.slice(0, head)}…${str.slice(-tail)}`;
}

function formatRelative(iso) {
 const t = new Date(iso).getTime();
 if (!Number.isFinite(t)) return'—';
 const diff = Date.now() - t;
 const m = Math.round(diff / 60000);
 if (m < 1) return'just now';
 if (m < 60) return`${m}m ago`;
 const h = Math.round(m / 60);
 if (h < 24) return`${h}h ago`;
 const d = Math.round(h / 24);
 if (d < 30) return`${d}d ago`;
 return new Date(iso).toLocaleDateString();
}

function CallsList({ loading, conversations, selectedOp, onSelect, subscriptionId, appName, timeRange, onWiden }) {
 const canWiden = onWiden && widenTimeRange(timeRange,'30d') !== timeRange;
 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-3">
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-gray-700 truncate"
 title={appName || subscriptionId}>
 {appName ?`Calls · ${appName}` :'Calls'}
 </h3>
 {appName && subscriptionId && (
 <p className="text-[10px] font-mono text-gray-400 truncate"
 title={subscriptionId}>
 {subscriptionId}
 </p>
 )}
 {timeRange && (
 <p className="text-[11px] text-gray-400 mt-0.5">
 Window: last {timeRange}
 </p>
 )}
 </div>
 <div className="flex items-center gap-3">
 <span className="text-xs text-gray-400 tabular-nums">
 {Array.isArray(conversations) ? conversations.length.toLocaleString() : 0}
 </span>
 {Array.isArray(conversations) && conversations.length > 0 && (
 <ExportButton data={conversations} filename={`conversations-${subscriptionId}`} />
 )}
 </div>
 </div>

 {loading ? (
 <InlineLoader message="Loading calls…" />
 ) : !Array.isArray(conversations) || conversations.length === 0 ? (
 <div className="text-center py-10">
 <p className="text-xs text-gray-500">
 No calls found for this app in the last {timeRange ||'selected window'}.
 </p>
 {canWiden && (
 <button
 onClick={onWiden}
 className="mt-3 text-xs px-3 py-1.5 rounded border border-gray-300 
 hover:bg-gray-50 text-gray-700 
 transition"
 >
 Widen to last 30d
 </button>
 )}
 </div>
 ) : (
 <div className="max-h-[640px] overflow-y-auto -mx-2 px-1 space-y-1">
 {conversations.map((c) => (
 <CallRow
 key={c.operationId || c.timestamp}
 call={c}
 selected={selectedOp === c.operationId}
 onClick={() => onSelect(c.operationId)}
 />
 ))}
 </div>
 )}
 </div>
 );
}

function CallRow({ call, selected, onClick }) {
 const initials = getInitials(call.userName || call.userEmail);
 return (
 <button
 onClick={onClick}
 className={`w-full text-left p-3 rounded-lg border transition group
 ${selected
 ?'bg-brand-50 border-brand-300'
 :'bg-transparent border-transparent hover:bg-gray-50 hover:border-gray-200'}
`}
 >
 <div className="flex items-center gap-3">
 <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
 ${selected
 ?'bg-brand-600 text-white'
 :'bg-gray-200 text-gray-600'}`}>
 {initials}
 </div>
 <div className="flex-1 min-w-0">          <div className="flex items-center justify-between gap-2">
 <span className="text-sm font-medium truncate">
 {call.userEmail || call.userName ||'App / service call'}
 </span>
 <span className="text-[10px] text-gray-400 flex-shrink-0">
 {call.timestamp ? new Date(call.timestamp).toLocaleString() :'—'}
 </span>
 </div>
 <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
 {call.model && (
 <span className="font-mono text-[10px] px-1.5 py-0.5 rounded
 bg-gray-100 truncate max-w-[140px]">
 {call.model}
 </span>
 )}
 {call.totalTokens != null && (
 <span className="tabular-nums">
 {Number(call.totalTokens).toLocaleString()} tok
 </span>
 )}
 </div>
 </div>
 <ChevronRight className={`w-4 h-4 flex-shrink-0 transition
 ${selected ?'text-brand-600' :'text-gray-300 group-hover:text-gray-500'}`} />
 </div>
 </button>
 );
}

function CallDetailPanel({ operationId, loading, entries, error, tab, onTabChange, onClose, onJumpToSubscription, resolveAppName }) {
 // Try to discover the SubscriptionId from the trace itself — useful when
 // the user deep-linked from RealTime / UserActivity with only an operationId.
 const inferredSubscriptionId = useMemo(() => {
 if (!Array.isArray(entries)) return null;
 for (const e of entries) {
 const cd = e.customDimensions;
 if (cd && typeof cd ==='object') {
 const v = cd['Subscription ID'] || cd['SubscriptionId'] || cd['subscriptionId'];
 if (v) return String(v);
 }
 const j = e.messageJson;
 if (j && typeof j ==='object') {
 const v = j['subscription-id'] || j.subscriptionId || j.appClientId;
 if (v) return String(v);
 }
 }
 return null;
 }, [entries]);

 // Hooks must run unconditionally — keep these *before* the early returns
 // below so the hook order stays stable across renders.`messages` and the
 // inferred-app-name lookup are cheap when`entries` is empty/missing.
 const messages = useMemo(() => extractChatMessages(entries), [entries]);
 const callSummary = useMemo(() => extractCallSummary(entries), [entries]);
 // The parent only knows the URL-supplied subscriptionId. If we discover a
 // different (or any) id from the trace, fetch its name on the fly so the
 // header doesn't fall back to a raw GUID on operation-only deep links.
 const { data: inferredAppList } = useApiQuery(
 ['conversation-apps','inferred', inferredSubscriptionId ||''],
 () => api.getConversationApps(inferredSubscriptionId ? [inferredSubscriptionId] : []),
 { enabled: !!inferredSubscriptionId, staleTime: 5 * 60 * 1000 },
 );
 const inferredAppName = useMemo(() => {
 const fromParent = resolveAppName ? resolveAppName(inferredSubscriptionId) : null;
 if (fromParent) return fromParent;
 if (!inferredSubscriptionId || !Array.isArray(inferredAppList)) return null;
 const id = String(inferredSubscriptionId).toLowerCase();
 const hit = inferredAppList.find(
 (a) => a?.id && (a.id === inferredSubscriptionId || a.id.toLowerCase() === id),
 );
 return hit?.name || null;
 }, [inferredAppList, inferredSubscriptionId, resolveAppName]);

 if (!operationId) {
 return (
 <div className="chart-container">
 <div className="text-center py-16">
 <div className="inline-flex items-center justify-center w-12 h-12 rounded-full
 bg-gray-100 text-gray-400 mb-4">
 <MessageSquare className="w-6 h-6" />
 </div>
 <h3 className="text-sm font-semibold text-gray-700">
 Select a call
 </h3>
 <p className="text-xs text-gray-500 mt-1">
 Pick any call on the left to see its full transcript and metadata.
 </p>
 </div>
 </div>
 );
 }

 if (loading) {
 return (
 <div className="chart-container">
 <InlineLoader message="Loading call detail…" />
 </div>
 );
 }

 if (error) {
 return (
 <div className="chart-container">
 <div className="flex items-start gap-3 text-red-600">
 <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
 <div>
 <div className="font-medium text-sm">Failed to load call detail</div>
 <div className="text-xs mt-1">{error.message ||'Unknown error'}</div>
 </div>
 </div>
 </div>
 );
 }

 if (!Array.isArray(entries) || entries.length === 0) {
 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-3">
 <OperationHeader operationId={operationId} />
 <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
 <X className="w-4 h-4" />
 </button>
 </div>
 <div className="text-center py-10 text-xs text-gray-400">
 No trace entries found for this operation. It may be outside the workspace's
 retention window, or it may belong to a different Application Insights resource.
 </div>
 </div>
 );
 }

 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
 <OperationHeader
 operationId={operationId}
 subscriptionId={inferredSubscriptionId}
 appName={inferredAppName}
 />
 <div className="flex items-center gap-2">
 {onJumpToSubscription && inferredSubscriptionId && (
 <button
 onClick={() => onJumpToSubscription(inferredSubscriptionId)}
 className="text-xs px-2.5 py-1 rounded border border-gray-300 
 hover:bg-gray-50 text-gray-700 
 transition"
 title={inferredAppName ?`Show all calls for ${inferredAppName}` :'Show all calls for this app'}
 >
 View all calls
 </button>
 )}
 <ExportButton data={entries} filename={`call-${operationId}`} />
 <button
 onClick={onClose}
 className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
 title="Close"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>

 {/* At-a-glance call summary — status, latency, user, roles, model, tokens */}
 {callSummary && <CallSummaryStrip summary={callSummary} />}

 {/* Tabs */}
 <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
 <TabButton active={tab ==='chat'} onClick={() => onTabChange('chat')}>
 Conversation
 {messages.length > 0 && (
 <span className="ml-1.5 text-[10px] text-gray-400">({messages.length})</span>
 )}
 </TabButton>
 <TabButton active={tab ==='raw'} onClick={() => onTabChange('raw')}>
 Trace ({entries.length})
 </TabButton>
 <TabButton active={tab ==='meta'} onClick={() => onTabChange('meta')}>
 Metadata
 </TabButton>
 </div>

 {tab ==='chat' && <ChatView messages={messages} entries={entries} />}
 {tab ==='raw' && <RawTraceView entries={entries} />}
 {tab ==='meta' && <MetadataView entries={entries} />}
 </div>
 );
}

function OperationHeader({ operationId, subscriptionId, appName }) {
 const [copied, setCopied] = useState(false);
 const copy = () => {
 navigator.clipboard.writeText(operationId);
 setCopied(true);
 setTimeout(() => setCopied(false), 1200);
 };
 return (
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-gray-700">Call Detail</h3>
 <button
 onClick={copy}
 title="Copy Operation ID"
 className="mt-0.5 flex items-center gap-1.5 text-[11px] font-mono text-gray-500
 hover:text-brand-600 group"
 >
 <Hash className="w-3 h-3" />
 <span className="truncate max-w-[280px]">{operationId}</span>
 {copied
 ? <Check className="w-3 h-3 text-emerald-500" />
 : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />}
 </button>
 {subscriptionId && (
 <div className="mt-0.5 text-[11px] text-gray-400 truncate max-w-[360px]"
 title={appName ?`${appName} · ${subscriptionId}` :`App ${subscriptionId}`}>
 {appName ? (
 <>App <span className="text-gray-600 font-medium">{appName}</span></>
 ) : (
 <>App <span className="font-mono">{subscriptionId}</span></>
 )}
 </div>
 )}
 </div>
 );
}

function TabButton({ active, onClick, children }) {
 return (
 <button
 onClick={onClick}
 className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition
 ${active
 ?'border-brand-600 text-brand-700'
 :'border-transparent text-gray-500 hover:text-gray-700'}`}
 >
 {children}
 </button>
 );
}

/**
 * At-a-glance summary of a single call. Pulls together the operational facts
 * scattered across the trace entries (HTTP status, latency, user + roles,
 * model, token usage, region, time) into one compact strip so the reviewer
 * doesn't have to open the Trace/Metadata tabs for the basics.
 */
function CallSummaryStrip({ summary: s }) {
 const statusOk = s.status != null && s.status < 400;
 const statusTone = s.status == null
 ?'bg-gray-100 text-gray-500'
 : statusOk
 ?'bg-emerald-50 text-emerald-700 border border-emerald-200'
 :'bg-red-50 text-red-700 border border-red-200';

 const fmtDuration = (ms) => {
 if (ms == null) return null;
 if (ms < 1000) return`${Math.round(ms)} ms`;
 return`${(ms / 1000).toFixed(2)} s`;
 };

 return (
 <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50/60 p-3">
 {/* Top row: status · method/path · latency · time */}
 <div className="flex flex-wrap items-center gap-2 text-xs">
 {s.status != null && (
 <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold tabular-nums ${statusTone}`}>
 {statusOk ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
 HTTP {s.status}
 </span>
 )}
 {(s.method || s.path) && (
 <span className="font-mono text-[11px] text-gray-600 truncate max-w-[280px]"
 title={`${s.method ||''} ${s.path ||''}`.trim()}>
 {s.method && <span className="font-semibold">{s.method} </span>}
 {s.path}
 </span>
 )}
 {s.durationMs != null && (
 <span className="inline-flex items-center gap-1 text-gray-500 tabular-nums">
 <Clock className="w-3 h-3" />{fmtDuration(s.durationMs)}
 </span>
 )}
 {s.timestamp && (
 <span className="ml-auto text-gray-400 text-[11px]" title={new Date(s.timestamp).toLocaleString()}>
 {formatRelative(s.timestamp)}
 </span>
 )}
 </div>

 {/* Middle row: user + auth */}
 {(s.user || s.userName) && (
 <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
 <span className="inline-flex items-center gap-1 text-gray-600">
 <User className="w-3 h-3" />
 <span className="font-medium">{s.userName || s.user ||'—'}</span>
 {s.user && s.userName && <span className="text-gray-400">({s.user})</span>}
 </span>
 {s.authMethod && (
 <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono text-[10px]">
 {s.authMethod}
 </span>
 )}
 </div>
 )}

 {/* Bottom row: model + token usage + region */}
 {(s.model || s.totalTokens != null || s.region) && (
 <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
 {s.model && (
 <span className="inline-flex items-center gap-1">
 <Bot className="w-3 h-3" />
 <span className="font-mono">{s.model}</span>
 </span>
 )}
 {s.promptTokens != null && (
 <span className="tabular-nums">{s.promptTokens.toLocaleString()} in</span>
 )}
 {s.completionTokens != null && (
 <span className="tabular-nums">{s.completionTokens.toLocaleString()} out</span>
 )}
 {s.totalTokens != null && (
 <span className="tabular-nums font-semibold text-gray-700">
 {s.totalTokens.toLocaleString()} tokens
 </span>
 )}
 {s.totalTokens != null && s.promptTokens == null && s.completionTokens == null && (
 <span
 className="text-gray-400 cursor-help"
 title="This model (Responses API) reports only a total token count, not an input/output split."
 >
 (no in/out split)
 </span>
 )}
 {s.region && (
 <span className="ml-auto text-gray-400">{s.region}</span>
 )}
 </div>
 )}
 </div>
 );
}

// ── Chat transcript view ────────────────────────────────────────────────────

function ChatView({ messages, entries }) {
 // Try to grab token usage from the same trace we pulled messages from.
 const usage = useMemo(() => {
 // The gateway emits prompt/completion tokens as the literal string "N/A"
 // for some models (only total is a real number). Coerce those — and any
 // empty/non-numeric value — to null so we render nothing instead of "NaN".
 const num = (v) => {
 if (v == null || v ==='N/A' || v ==='') return null;
 const n = Number(v);
 return Number.isFinite(n) ? n : null;
 };
 for (const e of entries) {
 const j = e.messageJson;
 if (!j || typeof j !=='object') continue;
 const pt = num(j['prompt-tokens'] ?? j.promptTokens ?? j.usage?.prompt_tokens);
 const ct = num(j['completion-tokens'] ?? j.completionTokens ?? j.usage?.completion_tokens);
 const tt = num(j['total-tokens'] ?? j.totalTokens ?? j.usage?.total_tokens);
 if (pt != null || ct != null || tt != null) {
 return { prompt: pt, completion: ct, total: tt, model: j.model || null };
 }
 }
 return null;
 }, [entries]);

 if (messages.length === 0) {
 return (
 <div className="text-center py-10 text-xs text-gray-400">
 No chat messages were captured for this call. Switch to the{''}
 <span className="font-semibold">Trace</span> tab to see the raw entries.
 </div>
 );
 }

 return (
 <div className="space-y-4">
 {usage && (
 <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 
 bg-gray-50 rounded-lg px-3 py-2">
 {usage.model && (
 <span className="flex items-center gap-1.5">
 <Bot className="w-3.5 h-3.5" />
 <span className="font-mono">{usage.model}</span>
 </span>
 )}
 {usage.prompt != null && (
 <span className="tabular-nums">{usage.prompt.toLocaleString()} prompt</span>
 )}
 {usage.completion != null && (
 <span className="tabular-nums">{usage.completion.toLocaleString()} completion</span>
 )}
 {usage.total != null && (
 <span className="tabular-nums font-semibold text-gray-700">
 {usage.total.toLocaleString()} total
 </span>
 )}
 {usage.total != null && usage.prompt == null && usage.completion == null && (
 <span
 className="text-gray-400 cursor-help"
 title="This model (Responses API) reports only a total token count, not a prompt/completion split."
 >
 (no prompt/completion split)
 </span>
 )}
 </div>
 )}
 <div className="space-y-3">
 {messages.map((m, i) => (
 <ChatMessage key={i} message={m} />
 ))}
 </div>
 </div>
 );
}

function ChatMessage({ message }) {
 const isUser = message.role ==='user';
 const isAssistant = message.role ==='assistant';
 const isSystem = message.role ==='system';

 const roleStyles = isUser
 ?'bg-brand-50 border-brand-200'
 : isAssistant
 ?'bg-emerald-50 border-emerald-200'
 :'bg-gray-50 border-gray-200';

 const icon = isUser
 ? <User className="w-3.5 h-3.5" />
 : isAssistant
 ? <Bot className="w-3.5 h-3.5" />
 : <MessageSquare className="w-3.5 h-3.5" />;

 return (
 <div className={`rounded-lg border ${roleStyles} p-3`}>
 <div className="flex items-center gap-2 mb-2">
 <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide
 text-gray-600">
 {icon}
 {message.role ||'message'}
 </span>
 {isSystem && (
 <span className="text-[10px] text-gray-400">(instructions)</span>
 )}
 </div>
 <div className="text-sm whitespace-pre-wrap break-words text-gray-800 leading-relaxed">
 {message.content || <span className="italic text-gray-400">(empty)</span>}
 </div>
 {message.truncated && (
 <div className="mt-2 text-[10px] text-amber-600 flex items-center gap-1">
 <AlertTriangle className="w-3 h-3" />
 Body truncated by the gateway (~8KB limit) — content may be incomplete.
 </div>
 )}
 </div>
 );
}

// ── Raw trace view ──────────────────────────────────────────────────────────

function RawTraceView({ entries }) {
 return (
 <div className="space-y-3">
 {entries.map((entry, idx) => (
 <TraceEntry key={idx} entry={entry} />
 ))}
 </div>
 );
}

function TraceEntry({ entry }) {
 const isError = entry.resultCode && Number(entry.resultCode) >= 400;
 return (
 <div className={`rounded-lg border p-3
 ${isError
 ?'bg-red-50 border-red-200'
 :'bg-gray-50 border-gray-200'}`}>
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <span className="text-[10px] font-mono uppercase tracking-wide
 px-1.5 py-0.5 rounded bg-gray-200 
 text-gray-700">
 {entry.itemType ||'trace'}
 </span>
 {entry.timestamp && (
 <span className="text-[11px] text-gray-500 flex items-center gap-1">
 <Clock className="w-3 h-3" />
 {new Date(entry.timestamp).toLocaleString()}
 </span>
 )}
 {entry.resultCode && (
 <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded
 ${isError
 ?'bg-red-100 text-red-700'
 :'bg-emerald-100 text-emerald-700'}`}>
 {entry.resultCode}
 </span>
 )}
 {entry.duration != null && (
 <span className="text-[11px] text-gray-400 tabular-nums">
 {Math.round(entry.duration)} ms
 </span>
 )}
 </div>
 {entry.name && (
 <div className="text-sm font-medium mb-2 break-all">{entry.name}</div>
 )}
 {entry.message && (
 <JsonBlock value={entry.messageJson ?? entry.message} />
 )}
 </div>
 );
}

// ── Metadata view ───────────────────────────────────────────────────────────

function MetadataView({ entries }) {
 const merged = useMemo(() => {
 const out = {};
 for (const e of entries) {
 if (e.customDimensions && typeof e.customDimensions ==='object') {
 Object.assign(out, e.customDimensions);
 }
 }
 return out;
 }, [entries]);

 const rows = Object.entries(merged).sort(([a], [b]) => a.localeCompare(b));

 if (rows.length === 0) {
 return (
 <div className="text-center py-10 text-xs text-gray-400">
 No custom dimensions captured for this call.
 </div>
 );
 }

 return (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <tbody>
 {rows.map(([k, v]) => (
 <tr key={k} className="border-b border-gray-100 last:border-0">
 <td className="py-1.5 pr-4 font-mono text-gray-500 align-top whitespace-nowrap">
 {k}
 </td>
 <td className="py-1.5 font-mono break-all text-gray-800">
 {typeof v ==='object' ? JSON.stringify(v) : String(v)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function JsonBlock({ value }) {
 const text = typeof value ==='string'
 ? value
 : JSON.stringify(value, null, 2);
 return (
 <pre className="text-[11px] bg-white p-2.5 rounded border
 border-gray-200 overflow-x-auto font-mono
 max-h-72 overflow-y-auto leading-relaxed">
 {text}
 </pre>
 );
}

function getInitials(input) {
 if (!input) return'?';
 const cleaned = String(input).split('@')[0].replace(/[._-]+/g,'').trim();
 const parts = cleaned.split(/\s+/).filter(Boolean);
 if (parts.length === 0) return'?';
 if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
 return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Roll the call's trace entries up into a single at-a-glance summary. Every
 * field here was verified to be present in the gateway traces (AppRequests +
 * the APIAccess / ai-gateway / APIResponse AppTraces): HTTP status, latency,
 * the calling user + their Entra roles, model, token usage, region and
 * timestamp. Surfacing them means a viewer sees the operational shape of the
 * call without having to dig through the Trace / Metadata tabs.
 */
function extractCallSummary(entries) {
 if (!Array.isArray(entries) || entries.length === 0) return null;
 const s = {
 status: null, durationMs: null, method: null, path: null,
 user: null, userName: null, authMethod: null,
 model: null, promptTokens: null, completionTokens: null, totalTokens: null,
 region: null, apiName: null, timestamp: null,
 };

 for (const e of entries) {
 if (!s.timestamp && e.timestamp) s.timestamp = e.timestamp;

 // AppRequests carries the HTTP outcome + latency + route.
 if ((e.itemType ==='AppRequests' || e.name) && e.resultCode != null && s.status == null) {
 s.status = Number(e.resultCode);
 }
 if (e.duration != null && s.durationMs == null) s.durationMs = Number(e.duration);
 if (e.name && !s.path) {
 const parts = String(e.name).split(' ');
 if (parts.length === 2) { s.method = parts[0]; s.path = parts[1]; }
 else s.path = e.name;
 }

 const cd = e.customDimensions;
 if (cd && typeof cd ==='object') {
 if (!s.region && cd.Region) s.region = cd.Region;
 if (!s.apiName && (cd['API Name'] || cd['API Type'])) s.apiName = cd['API Name'] || cd['API Type'];
 if (!s.method && cd['HTTP Method']) s.method = cd['HTTP Method'];
 }

 const j = e.messageJson;
 if (j && typeof j ==='object') {
 // APIAccess trace: user identity + roles.
 const u = j.user;
 if (u && typeof u ==='object') {
 if (!s.user && u.email) s.user = u.email;
 if (!s.userName && u.name) s.userName = u.name;
 } else if (typeof u ==='string' && !s.user) {
 s.user = u;
 }
 if (!s.authMethod && j.authMethod) s.authMethod = j.authMethod;
 if (!s.user && j.userEmail) s.user = j.userEmail;
 if (!s.userName && j.userName) s.userName = j.userName;

 // ai-gateway trace: model + tokens (values may be the string "N/A").
 if (!s.model && j.model) s.model = j.model;
 const num = (v) => (v == null || v ==='N/A' || v ==='' ? null : Number(v));
 const pt = num(j['prompt-tokens'] ?? j.usage?.prompt_tokens);
 const ct = num(j['completion-tokens'] ?? j.usage?.completion_tokens);
 const tt = num(j['total-tokens'] ?? j.usage?.total_tokens);
 if (pt != null && s.promptTokens == null) s.promptTokens = pt;
 if (ct != null && s.completionTokens == null) s.completionTokens = ct;
 if (tt != null && s.totalTokens == null) s.totalTokens = tt;

 // APIResponse trace: authoritative status + duration if not already set.
 if (s.status == null && j.statusCode != null) s.status = Number(j.statusCode);
 if (s.durationMs == null && j.durationMs != null) s.durationMs = Number(j.durationMs);
 }
 }

 // Return null only if we truly found nothing worth showing.
 const hasAnything = s.status != null || s.user || s.model || s.totalTokens != null || s.durationMs != null;
 return hasAnything ? s : null;
}

/**
 * Try to extract chat-style messages from trace entries. The OpenAI request/response
 * payloads typically live inside an AppTrace`message` JSON blob, but the exact
 * shape varies — we look for the most common locations.
 */
function extractChatMessages(entries) {
 const out = [];
 for (const e of entries || []) {
 const j = e.messageJson;
 if (!j || typeof j !=='object') continue;

 // Common: request.messages = [...]
 const requestMessages =
 j.messages
 ?? j.request?.messages
 ?? j.requestBody?.messages
 ?? j.input?.messages;
 if (Array.isArray(requestMessages)) {
 for (const m of requestMessages) {
 out.push(normalizeChatMessage(m));
 }
 } else if (typeof j.prompt ==='string') {
 out.push({ role:'user', content: j.prompt });
 }

 // Common: response.choices[].message
 const choices =
 j.choices
 ?? j.response?.choices
 ?? j.responseBody?.choices
 ?? j.output?.choices;
 if (Array.isArray(choices)) {
 for (const c of choices) {
 const msg = c?.message ?? (c?.text ? { role:'assistant', content: c.text } : null);
 if (msg) out.push(normalizeChatMessage(msg));
 }
 } else if (typeof j.completion ==='string') {
 out.push({ role:'assistant', content: j.completion });
 }
 }

 // Fallback / primary source in THIS deployment: the APIM gateway logs the
 // request + response payloads as custom dimensions on the AppRequests entry
 // (`Request-Body` / `Response-Body`). Verified 100% coverage for LLM calls
 // (bodies are truncated at ~8KB). The AppTrace `Message` blobs above rarely
 // carry the messages array, so without this the Conversation tab is empty.
 // We only mine these when the AppTrace path produced nothing, to avoid
 // duplicating messages when both are present.
 if (out.length === 0) {
 for (const e of entries || []) {
 const cd = e.customDimensions;
 if (!cd || typeof cd !=='object') continue;

 const reqBody = firstNonEmpty(cd,['Request-Body','RequestBody','request-body']);
 const respBody = firstNonEmpty(cd,['Response-Body','ResponseBody','response-body']);

 out.push(...messagesFromBody(reqBody,'request'));
 out.push(...messagesFromBody(respBody,'response'));
 }
 }

 return out;
}

/** Return the first non-empty value among the given keys of an object. */
function firstNonEmpty(obj, keys) {
 for (const k of keys) {
 const v = obj[k];
 if (typeof v ==='string' && v.trim()) return v;
 }
 return null;
}

/**
 * Parse an OpenAI request or response body (JSON string) into chat messages.
 * Handles both Chat Completions (`messages[]` / `choices[].message`) and the
 * newer Responses API (`input` / `output[].content[]`). If the body isn't
 * parseable JSON we still surface it as a single raw message so the user sees
 * *something* rather than a blank tab.
 *
 * @param {string|null} body  Raw body string from the gateway custom dimension.
 * @param {'request'|'response'} kind  Which side of the exchange this is.
 */
function messagesFromBody(body, kind) {
 if (!body || typeof body !=='string') return [];
 let j;
 try {
 j = JSON.parse(body);
 } catch {
 // Truncated (>8KB) or non-JSON — show it raw, labelled by side.
 return [{
 role: kind ==='request' ?'user' :'assistant',
 content: body,
 truncated: true,
 }];
 }
 const msgs = [];

 if (kind ==='request') {
 // Chat Completions: { messages: [...] }
 if (Array.isArray(j.messages)) {
 for (const m of j.messages) msgs.push(normalizeChatMessage(m));
 } else if (typeof j.input ==='string') {
 // Responses API with a plain string input.
 msgs.push({ role:'user', content: j.input });
 } else if (Array.isArray(j.input)) {
 // Responses API with structured input items.
 for (const item of j.input) msgs.push(normalizeChatMessage(item));
 } else if (typeof j.prompt ==='string') {
 msgs.push({ role:'user', content: j.prompt });
 }
 } else {
 // Response side. Chat Completions: { choices: [{ message }] }
 if (Array.isArray(j.choices)) {
 for (const c of j.choices) {
 const m = c?.message ?? (typeof c?.text ==='string' ? { role:'assistant', content: c.text } : null);
 if (m) msgs.push(normalizeChatMessage(m));
 }
 } else if (Array.isArray(j.output)) {
 // Responses API: output[].content[].text
 for (const item of j.output) {
 if (Array.isArray(item?.content)) {
 const text = item.content
 .map((p) => (typeof p?.text ==='string' ? p.text : ''))
 .filter(Boolean)
 .join('\n');
 if (text) msgs.push({ role: item.role ||'assistant', content: text });
 } else {
 msgs.push(normalizeChatMessage(item));
 }
 }
 } else if (typeof j.output_text ==='string') {
 msgs.push({ role:'assistant', content: j.output_text });
 } else if (typeof j.completion ==='string') {
 msgs.push({ role:'assistant', content: j.completion });
 }
 }

 return msgs;
}

function normalizeChatMessage(m) {
 if (!m || typeof m !=='object') {
 return { role:'unknown', content: String(m ??'') };
 }
 // Content can be a string or an array of {type, text} parts (multi-modal).
 let content = m.content;
 if (Array.isArray(content)) {
 content = content
 .map((p) => {
 if (typeof p ==='string') return p;
 if (p?.type ==='text' && typeof p.text ==='string') return p.text;
 if (typeof p?.text ==='string') return p.text;
 return JSON.stringify(p);
 })
 .join('\n');
 } else if (content == null && m.tool_calls) {
 content =`[tool_calls]\n${JSON.stringify(m.tool_calls, null, 2)}`;
 } else if (typeof content !=='string') {
 content = JSON.stringify(content, null, 2);
 }
 return { role: m.role ||'assistant', content };
}
